import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { LoadingIndicator } from '../../components/Spinner';
import { useMasterData } from '../masterdata/queries';
import { toneForPriorityLevel, toneForStateCategory } from './presentation';
import { useBoard } from './queries';
import { TicketCustomerFilter } from './TicketCustomerFilter';
import { Select } from '../../components/Select';

/**
 * Die Kanban-Ansicht -- F1-34, K-31.
 *
 * Eine Spalte je Zustand des Mandanten, in der Reihenfolge der Stammdaten.
 * Leere Spalten bleiben stehen: Eine Ansicht, in der Spalten je nach
 * Fuellstand verschwinden, laesst sich nicht wiedererkennen.
 *
 * **Ohne Ziehen und Ablegen.** Ein Zustandswechsel ist eine benannte Aktion
 * mit Vorbedingungen, Protokoll und ab Phase 4 einer SLA-Uhr (docs/05,
 * Abschnitt 2) -- ein versehentliches Ziehen waere dafuer eine denkbar
 * schlechte Ausloesung. Die Karten fuehren ins Ticket, dort steht die
 * Aktion.
 *
 * Die Zahl an der Spalte zaehlt **alles**, die Karten zeigen einen
 * Ausschnitt. Ohne diese Zahl saehe eine Spalte mit dreihundert Tickets aus
 * wie eine mit zwanzig.
 *
 * Die Kundenauswahl steht in der Adresse und kommt aus der Liste mit
 * (F1-56); die Warteschlange bleibt eine Einstellung der Seite.
 */
export function BoardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customer } = useSearch({ from: '/app/board' });
  const [queueId, setQueueId] = useState('');
  const queues = useMasterData('queue');
  const query = useBoard({
    ...(queueId ? { queue_id: queueId } : {}),
    ...(customer ? { customer_id: customer } : {}),
  });

  return (
    <>
      <PageHeader
        title={t('board.title')}
        action={
          <Link to="/app/tickets" search={customer ? { customer } : {}} className="nav__item">
            {t('board.backToList')}
          </Link>
        }
      />

      <TicketCustomerFilter
        customerId={customer ?? ''}
        onChange={(next) => {
          void navigate({
            to: '/app/board',
            search: next ? { customer: next } : {},
            replace: true,
          });
        }}
      />

      <div className="toolbar">
        <label className="field__label" htmlFor="board-queue">
          {t('tickets.queue')}
        </label>
        <Select
          id="board-queue"
          className="field__control"
          value={queueId}
          onChange={(event) => {
            setQueueId(event.target.value);
          }}
        >
          <option value="">{t('board.allQueues')}</option>
          {(queues.data ?? []).map((queue) => (
            <option key={queue.id} value={queue.id}>
              {queue.name}
            </option>
          ))}
        </Select>
      </div>

      {query.isPending ? <LoadingIndicator /> : null}
      {query.isError ? <ErrorNotice error={query.error} /> : null}

      {query.isSuccess ? (
        <div className="board">
          {query.data.columns.map((column) => (
            <section key={column.state_id} className="board__column">
              <header className="board__head">
                <Badge tone={toneForStateCategory(column.state_category)} dot>
                  {column.state_name}
                </Badge>
                <span className="board__count">{column.total}</span>
              </header>

              {column.items.length === 0 ? (
                <p className="table__muted">{t('board.emptyColumn')}</p>
              ) : null}

              <ul className="board__cards">
                {column.items.map((ticket) => (
                  <li key={ticket.id} className="board__card">
                    <Link to="/app/tickets/$ticketId" params={{ ticketId: ticket.id }}>
                      <span className="table__number">{ticket.ticket_number}</span>
                      <span className="board__title">{ticket.title}</span>
                    </Link>
                    <div className="row">
                      <Badge tone={toneForPriorityLevel(ticket.priority.level)}>
                        {ticket.priority.name}
                      </Badge>
                      {ticket.is_major_incident ? (
                        <Badge tone="danger" dot>
                          {t('tickets.majorIncident')}
                        </Badge>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              {column.total > column.items.length ? (
                <p className="table__muted">
                  {t('board.more', { count: column.total - column.items.length })}
                </p>
              ) : null}
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}
